import { VIDEO_SYNC_MONITOR_API_PATH } from 'constants/api';
import { ApiService } from 'service/api/apiService';
import { FetchResult } from '../../../../shared/type/apiType';
import {
  BulkRetryRequest,
  BulkRetryResponse,
  TestSyncByTrackerRequest,
  TestSyncRequest,
  TestSyncResponse,
  VideoSyncStats,
  VideoSyncTracker,
} from '../types/videoSyncMonitorType';

// KCP/Mediahold source list
const KCP_MEDIAHOLD_SOURCES = ['kcp', 'mediahold'];

class VideoSyncMonitorService extends ApiService<VideoSyncTracker> {
  constructor() {
    super(VIDEO_SYNC_MONITOR_API_PATH);
  }

  /**
   * Injects kcp/mediahold source filter into URLSearchParams.
   * Adds ingest_source=kcp,mediahold if the user hasn't selected a specific source.
   */
  private injectSourceFilter(params?: URLSearchParams): URLSearchParams {
    const newParams = new URLSearchParams(params?.toString() || '');
    if (!newParams.has('ingest_source')) {
      newParams.set('ingest_source', KCP_MEDIAHOLD_SOURCES.join(','));
    }
    return newParams;
  }

  public async fetch(queryParams?: URLSearchParams): Promise<FetchResult<VideoSyncTracker>> {
    return super.fetch(this.injectSourceFilter(queryParams));
  }

  public async getStats(params?: URLSearchParams): Promise<VideoSyncStats> {
    return this.fetchBySubPathRaw<VideoSyncStats>('stats/', this.injectSourceFilter(params));
  }

  public async retry(id: number): Promise<VideoSyncTracker> {
    return this.createBySubPath<VideoSyncTracker, object>(`${id}/retry`, {});
  }

  public async bulkRetry(ids: number[]): Promise<BulkRetryResponse> {
    return this.createBySubPath<BulkRetryResponse, BulkRetryRequest>('bulk_retry', { ids });
  }

  public async getFailed(
    params?: URLSearchParams
  ): Promise<{ results: VideoSyncTracker[]; count: number }> {
    return this.fetchBySubPath<VideoSyncTracker>('failed/', this.injectSourceFilter(params));
  }

  public async getPending(
    params?: URLSearchParams
  ): Promise<{ results: VideoSyncTracker[]; count: number }> {
    return this.fetchBySubPath<VideoSyncTracker>('pending/', this.injectSourceFilter(params));
  }

  public async testSync(data: TestSyncRequest): Promise<TestSyncResponse> {
    return this.createBySubPath<TestSyncResponse, TestSyncRequest>('test_sync', data);
  }

  public async testSyncByTracker(data: TestSyncByTrackerRequest): Promise<TestSyncResponse> {
    return this.createBySubPath<TestSyncResponse, TestSyncByTrackerRequest>(
      'test_sync_by_tracker',
      data
    );
  }
}

export const videoSyncMonitorService = new VideoSyncMonitorService();
